/**
 * CR-6266: the target a dashboard week / month row or tile is judged against.
 */
import type { PeriodKPITargets } from '../../lib/api';

/**
 * The target for one week or month row of the Scheduled / PO Lines charts:
 * the row's own (the target in force at its end, stamped by the API) when
 * present, else the dashboard's single target. OTD's when the tenant runs
 * OTD-only, OTIF's otherwise — the charts draw one shared target line.
 *
 * @example
 * target: periodTarget(week, deliveryMode, chartTarget)
 *
 * @param row - a week or month row from the dashboard payload
 * @param deliveryMode - the tenant's delivery_metric_mode
 * @param fallback - the dashboard's single target for that metric
 * @returns the target percentage to draw for the row
 */
export function periodTarget(row: object, deliveryMode: string | undefined, fallback: number): number {
  const targets = row as PeriodKPITargets;
  return (deliveryMode === 'otd_only' ? targets.otd_target : targets.otif_target) ?? fallback;
}

/**
 * A tile block's own OTIF or OTD target — the target at the end of its
 * window (a past week or month picked with the offset included) — else the
 * dashboard's single target.
 *
 * @example
 * const otifTarget = blockTarget(data.current_week, 'otif', data.otif_target ?? 95);
 *
 * @param block - a tile block from the payload (current_week, current_month, …)
 * @param kpi - which target
 * @param fallback - the dashboard's single target for that metric
 * @returns the target percentage for the tile
 */
export function blockTarget(block: object | null | undefined, kpi: 'otif' | 'otd', fallback: number): number {
  const targets = (block ?? {}) as PeriodKPITargets;
  return (kpi === 'otd' ? targets.otd_target : targets.otif_target) ?? fallback;
}

/**
 * The one target every point of a chart shares, or null when the targets
 * change across it. A shared target is drawn as the full-width dashed line
 * the charts always had; a changing one as a stepped line.
 *
 * @param points - the chart points, each with its `target`
 * @returns the shared target, or null
 */
export function sharedTarget(points: Array<{ target?: number | null }>): number | null {
  const values = points.map((p) => p.target).filter((v): v is number => typeof v === 'number');
  if (values.length === 0) return null;
  return values.every((v) => v === values[0]) ? values[0] : null;
}
